"use client";

import React, { ReactNode, useMemo } from "react";
import { ILayout } from "/models/layout/ILayout";
import {
  ApplicationSettings,
  ApplicationType,
  Footer,
  Header,
  ISocialMediaLinks
} from "@into-discovery/browsewebshared";
import { IHeader } from "@into-discovery/browsewebshared/src/models/header/IHeader";
import color from "../../styles/_helpers/branding.module.scss";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "/navigation";
import { getLegalURLs, getPageURLs, LinkItem, localesList } from "/util/menu-items";
import { appBranding } from "/constants/application-branding";
import { useSearchParams } from "next/navigation";
import { getProtocol } from "/util/get-protocol";
import getMicrositeBrandThemeSettings from "/util/microsite-branding";

type Props = {
  pageProps: ILayout;
  children: ReactNode;
  domain: string;
};

export default function LayoutProvider({ pageProps, children, domain }: Props) {
  const { router, currentLocale, pathName, searchParams } = useAppRouterData();
  const legalURLs = useMemo(() => getLegalURLs({ currentLocale }), [currentLocale]);
  const translate = useTranslations(`Common`);
  const headerApplyCopyText = translate(`header.nav.apply`);
  const headerSearchPlaceholder =
    pageProps?.content?.brand?.countryCode === "US"
      ? translate(`header.search-placeholder-us`)
      : translate(`header.search-placeholder-uk`);
  const protocol = getProtocol();

  const appSettings = useMemo(
    () => new ApplicationSettings(process.env.MAPS_API_KEY, appBranding, ApplicationType.UniBrandedB2c),
    []
  );

  const combinedMenuItems = useMemo(() => {
    const courseLevelMenuItems = pageProps?.courseLevelMenuItems || [];
    const links: LinkItem[] = pageProps?.content?.micrositeLinks?.linksCollection?.links || [];

    const pageURLs = getPageURLs(links, currentLocale);

    return [...courseLevelMenuItems, ...pageURLs];
  }, [currentLocale, pageProps]);

  const {
    brandLogo,
    brandLogoAltText,
    brandFacebookURL,
    brandInstagramURL,
    themeColor,
    themeCopyColor,
    invertedTheme,
    brandPrimaryColor,
    headerLayout
  } = extractBranding(pageProps);

  const headerProps: IHeader = {
    router,
    searchParams,
    menuItems: combinedMenuItems,
    localesList,
    translate: useTranslations("Common"),
    isShowSearchBar: true,
    applicationSettings: appSettings,
    currentLocale,
    pathname: pathName,
    applicationURL: `${protocol}://${domain}`,
    brandLogo,
    brandLogoAltText,
    themeColor,
    themeCopyColor,
    invertedTheme,
    brandPrimaryColor,
    headerApplyURL: `/${currentLocale}/search`,
    headerApplyCopyText,
    headerSearchPlaceholder,
    headerLayout
  };

  const footerProps = {
    intoLogoURL: process.env.NEXT_PUBLIC_INTO_GLOBAL_URL,
    legalURLs,
    currentLocale,
    socialURLs: {
      facebook: { url: brandFacebookURL ?? "", title: "Follow us on Facebook" },
      instagram: { url: brandInstagramURL ?? "", title: "Follow us on Instagram" }
    } as ISocialMediaLinks,
    themeColor: color.CopyColor,
    themeCopyColor: color.White,
    textICP: "沪ICP备14028023号-3",
    showB2C: true,
    countryCode: pageProps.content?.brand?.countryCode,
    application: ApplicationType.UniBrandedB2c
  };

  return (
    <>
      <Header headerProps={headerProps} />
      <main className="pb-4">{children}</main>
      <Footer footerProps={footerProps} />
    </>
  );
}

function useAppRouterData() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const currentLocale = useLocale();
  const pathName = usePathname();

  return { router, searchParams, currentLocale, pathName };
}

function extractBranding(pageProps: ILayout) {
  const content = pageProps?.content;
  const { themeColor, themeCopyColor, brandPrimaryColor, invertedTheme } = getMicrositeBrandThemeSettings(content);

  return {
    brandLogo: content.colorLogo?.image[0]?.secure_url,
    brandLogoAltText: content.colorLogo?.altText,
    brandFacebookURL: content.brandFacebookUrl,
    brandInstagramURL: content.brandInstagramUrl,
    themeColor,
    themeCopyColor,
    invertedTheme,
    brandPrimaryColor,
    headerLayout: content.headerLayout
  };
}
